/**
 * Wrapped input with label and error display.
 *
 * Props:
 *   id            — ties the <label> htmlFor to the <input> id (required for a11y)
 *   label         — label text; omit to skip the label element
 *   error         — error string (message rendered below the field) OR boolean true
 *                   (apply error styling + aria-invalid only, render no message)
 *   endAdornment  — optional ReactNode rendered to the right, inside the field wrapper
 *   className     — extra class(es) on the wrapping <div>
 *   inputClassName — extra class(es) on the <input> element itself (e.g. a readonly
 *                   dimmed style); opt-in, empty by default
 *
 * All other props (type, value, onChange, placeholder, disabled, ...) pass to <input>.
 */
import { forwardRef } from 'react';

const Input = forwardRef(function Input(
  { id, label, error, endAdornment, className = '', inputClassName = '', ...props },
  ref
) {
  const hasError = Boolean(error);
  const errorMessage = typeof error === 'string' && error ? error : null;

  const field = (
    <input
      id={id}
      ref={ref}
      className={[
        'form-input',
        hasError ? 'form-input--error' : '',
        endAdornment ? 'form-input--has-end-adornment' : '',
        inputClassName,
      ].filter(Boolean).join(' ')}
      aria-invalid={hasError ? 'true' : undefined}
      aria-describedby={errorMessage ? `${id}-error` : undefined}
      {...props}
    />
  );

  return (
    <div className={['form-group', className].filter(Boolean).join(' ')}>
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
        </label>
      )}
      {endAdornment ? (
        <div className="form-input-wrap">
          {field}
          {endAdornment}
        </div>
      ) : (
        field
      )}
      {errorMessage && (
        <p id={`${id}-error`} className="form-error" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
});

export default Input;
